import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { colors } from '../../styles/theme';
import { getCampaignBreakdowns, getMetaAdSets } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { readMetaAdSets, readMetaAgeGender, readMetaGeo } from '../metaAnalysis';
import { formatMetaMoney } from '../metaAdsMoney';
import { formatMetaCount, formatMetaPct, metaPlural } from '../metaFormat';
import { isMetaTodayPeriod } from '../metaPeriod';
import { MetaBarList, MetaBlockState, MetaCard, MetaDonut, MetaEmptyLine, MetaFootnote, MetaSoftNotice, MetaTable } from './MetaDataParts';
import { MetaAdSetsSkeleton, MetaBarsSkeleton, MetaDonutSkeleton, MetaTableSkeleton } from './MetaSkeletons';
import { metaAdsBodyTextSx, metaAdsMutedTextSx, metaAdsQuietButtonSx } from './metaAdsUi.styles';
import { metaFractions, metaRowValueText, validMetaScope } from './metaAnalysisUi';

const ADSETS_PAGE = 10;

/** Edad: una barra por tramo (de menor a mayor edad). */
export function AgeCard({ read, state }) {
  const { ages, metric, currency } = read;
  const values = ages.map((a) => a[metric]);
  const fractions = metaFractions(values);
  return (
    <MetaCard title="Edad" caption={metric === 'contacts' ? (currency ? 'Contactos · Costo por contacto' : 'Contactos') : 'Impresiones'} testId="meta-audience-age">
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-audience-age-state" skeleton={<MetaBarsSkeleton rows={6} />}>
        {ages.length === 0 ? (
          <MetaEmptyLine testId="meta-audience-age-empty">Todavía no hay datos de edad para este período.</MetaEmptyLine>
        ) : (
          <MetaBarList
            testId="meta-audience-age-bars"
            rows={ages.map((a, i) => ({ key: a.key, label: a.label, fraction: fractions[i], valueText: metaRowValueText(a, metric, currency) }))}
          />
        )}
      </MetaBlockState>
    </MetaCard>
  );
}

/** Género: dona + leyenda con cantidades y porcentajes. */
export function GenderCard({ read, state }) {
  const { genders, metric } = read;
  const unit = metric === 'contacts' ? ['contacto', 'contactos'] : ['impresión', 'impresiones'];
  const segments = genders.map((g) => ({
    key: g.key,
    label: g.label,
    value: g.value,
    detail: `${metaPlural(g.value, unit[0], unit[1])}${g.pct != null ? ` · ${formatMetaPct(g.pct)}` : ''}`,
  }));
  const summary = segments.map((s) => `${s.label}: ${s.detail}`).join('; ');
  return (
    <MetaCard title="Género" testId="meta-audience-gender">
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-audience-gender-state" skeleton={<MetaDonutSkeleton />}>
        {genders.length === 0 ? (
          <MetaEmptyLine testId="meta-audience-gender-empty">Todavía no hay datos de género para este período.</MetaEmptyLine>
        ) : (
          <MetaDonut segments={segments} ariaLabel={`Género: ${summary}`} testId="meta-audience-gender-donut" />
        )}
      </MetaBlockState>
    </MetaCard>
  );
}

/**
 * País o región: tabla por contactos (y gasto si la moneda es una sola). Con `contacts` incompletos
 * (regiones: Meta no da WhatsApp por región) la tabla va por impresiones —Impresiones, Clics (todos) y Gasto—,
 * sin contactos ni costo por contacto, con la nota de la API abajo.
 */
export function PlaceCard({ title, firstColumn, rows, geo, state, testId, emptyText, pendingText, contacts = null }) {
  const showSpend = !geo.mixedCurrencies && geo.currency;
  const byImpressions = contacts != null && contacts.complete === false;
  const columns = [
    { key: 'name', label: firstColumn },
    ...(byImpressions
      ? [{ key: 'impressions', label: 'Impresiones' }, { key: 'clicks', label: 'Clics (todos)' }]
      : [{ key: 'contacts', label: 'Contactos' }]),
    ...(showSpend ? [{ key: 'spend', label: 'Gasto' }] : []),
  ];
  const tableRows = rows.map((r) => ({
    key: r.key || r.label,
    cells: {
      name: r.label,
      ...(byImpressions
        ? { impressions: formatMetaCount(r.impressions || 0), clicks: formatMetaCount(r.clicks || 0) }
        : { contacts: formatMetaCount(r.contacts || 0) }),
      ...(showSpend ? { spend: formatMetaMoney(r.spend, geo.currency) } : {}),
    },
  }));
  return (
    <MetaCard title={title} caption={byImpressions && rows.length ? 'Por impresiones' : undefined} testId={testId}>
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId={`${testId}-state`} skeleton={<MetaTableSkeleton rows={4} />}>
        {rows.length === 0 ? (
          <MetaEmptyLine testId={`${testId}-empty`}>{geo.geoPending ? pendingText : emptyText}</MetaEmptyLine>
        ) : (
          <>
            <MetaTable columns={columns} rows={tableRows} testId={`${testId}-table`} />
            {byImpressions ? (
              <MetaFootnote testId={`${testId}-contacts-note`}>
                {contacts.note || 'Meta no informa todos los contactos por región: se muestran las impresiones.'}
              </MetaFootnote>
            ) : null}
          </>
        )}
      </MetaBlockState>
    </MetaCard>
  );
}

/** Público configurado por el anunciante en cada conjunto (NO es quién respondió). */
function ConfiguredCard({ state, adsets }) {
  const [shown, setShown] = useState(ADSETS_PAGE);
  const visible = adsets.slice(0, shown);
  return (
    <MetaCard
      title="Público configurado"
      testId="meta-audience-configured"
      action={(
        <Box component="span" sx={{ ...metaAdsMutedTextSx, fontSize: '11px', px: 1, py: '2px', borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.07)', whiteSpace: 'nowrap' }}>
          Definido por el anunciante
        </Box>
      )}
    >
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-audience-configured-state" skeleton={<MetaAdSetsSkeleton />}
        unavailableText="Todavía no disponible: el público configurado de cada conjunto se completa con la próxima actualización.">
        {adsets.length === 0 ? (
          <MetaEmptyLine testId="meta-audience-configured-empty">Todavía no hay conjuntos de anuncios para mostrar.</MetaEmptyLine>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {visible.map((a) => (
              <Box component="li" key={a.id} data-testid={`meta-adset-${a.id}`}
                sx={{ borderLeft: `2px solid ${colors.primary}`, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '0 8px 8px 0', px: 1.25, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600, fontSize: '13px', overflowWrap: 'anywhere' }}>
                  {a.name}
                  {a.adsCount != null ? <Box component="span" sx={{ fontWeight: 400, color: 'rgba(255,255,255,0.55)' }}>{` · ${metaPlural(a.adsCount, 'anuncio', 'anuncios')}`}</Box> : null}
                  {!a.isActive && a.statusLabel ? <Box component="span" sx={{ fontWeight: 400, color: 'rgba(255,255,255,0.55)' }}>{` · ${a.statusLabel}`}</Box> : null}
                </Typography>
                {a.lines.length ? a.lines.map((line) => (
                  <Typography key={line} sx={{ ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '17px' }}>{line}</Typography>
                )) : <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }}>Sin público configurado en Meta (Meta lo elige solo).</Typography>}
              </Box>
            ))}
          </Box>
        )}
        {adsets.length > shown ? (
          <Button size="small" onClick={() => setShown((n) => n + ADSETS_PAGE)} sx={{ ...metaAdsQuietButtonSx, color: colors.primaryLight }} data-testid="meta-audience-configured-more">
            {`Mostrar ${metaPlural(Math.min(ADSETS_PAGE, adsets.length - shown), 'conjunto más', 'conjuntos más')}`}
          </Button>
        ) : null}
      </MetaBlockState>
    </MetaCard>
  );
}

/**
 * Análisis · Público: edad, género, países, regiones y público configurado. Cada tipo se pide por
 * separado y solo al abrir la pantalla (`age_gender`, `geo` y `getmetaadsets`); una falla de uno
 * no afecta a los demás. Solo informativo.
 */
export default function MetaAudienceView({
  campaigns, scopeCampaignId, period, ownerUserId = null, includeInactive = false,
}) {
  const campaignId = validMetaScope(campaigns, scopeCampaignId);
  const deps = [period?.since, period?.until, campaignId, ownerUserId];
  const ageState = useMetaRead((signal) => getCampaignBreakdowns({ period, campaignId, breakdown: 'age_gender', ownerUserId, signal }), { deps });
  const geoState = useMetaRead((signal) => getCampaignBreakdowns({ period, campaignId, breakdown: 'geo', ownerUserId, signal }), { deps });
  const adsetsState = useMetaRead((signal) => getMetaAdSets({ period, campaignId, includeInactive, ownerUserId, signal }), { deps: [...deps, includeInactive] });
  const age = useMemo(() => readMetaAgeGender(ageState.res), [ageState.res]);
  const geo = useMemo(() => readMetaGeo(geoState.res), [geoState.res]);
  const { adsets } = useMemo(() => readMetaAdSets(adsetsState.res), [adsetsState.res]);
  // Sin ningún dato y sin pedidos en curso: un solo mensaje amable en vez de cuatro vacíos.
  const states = [ageState.status, geoState.status, adsetsState.status];
  const allUnavailable = states.every((s) => s === 'unavailable');
  return (
    <>
      {allUnavailable ? (
        <PanelEmptyState
          icon={PeopleOutlineIcon}
          title="Todavía no hay datos de público"
          hint="Todavía no están disponibles en este entorno. Se van a ver acá apenas estén."
          sx={{ minHeight: 240 }}
          testId="meta-analysis-audience-empty"
        />
      ) : (
        <>
          {isMetaTodayPeriod(period) ? (
            <MetaSoftNotice testId="meta-audience-today">Hoy los datos de público pueden estar incompletos; se completan mañana.</MetaSoftNotice>
          ) : null}
          <AgeCard read={age} state={ageState} />
          <GenderCard read={age} state={ageState} />
          <PlaceCard
            title="Países" firstColumn="País" rows={geo.countries} geo={geo} state={geoState} testId="meta-audience-countries"
            emptyText="Todavía no hay datos de países para este período."
            pendingText="Todavía no hay datos de países: se completan una vez por día, a las 6:00."
          />
          <PlaceCard
            title="Regiones" firstColumn="Región" rows={geo.regions} geo={geo} state={geoState} testId="meta-audience-regions" contacts={geo.regionContacts}
            emptyText="Todavía no hay datos de regiones para este período."
            pendingText="Todavía no hay datos de regiones: se completan una vez por día, a las 6:00."
          />
          <ConfiguredCard state={adsetsState} adsets={adsets} />
        </>
      )}
    </>
  );
}
